import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { get, useFormContext, Controller } from 'react-hook-form';
import { DayPicker } from 'react-day-picker';
import { format, isValid, parse } from 'date-fns';
import { HiOutlineCalendarDays } from 'react-icons/hi2';
import { useDebouncedSave } from '../hooks/useDebounce';

const VALUE_FORMAT = 'yyyy-MM-dd';

// The calendar is rendered into a portal on document.body rather than inside the card. DetailCard's
// value column carries cr-overflow-hidden and the card itself is only 14rem wide on lg, so an
// absolutely-positioned popover gets clipped. The portal keeps it out of that clipping context;
// `datepicker-overlay` is what stops DetailCard treating a click on the calendar as a click-outside
// (it is already in that component's overlaySelectors list).
const OVERLAY_CLASS = 'datepicker-overlay';

// Every part is classed explicitly instead of importing react-day-picker/style.css. The shipped
// stylesheet is driven by --rdp-* custom properties and sets its own font sizes/weights, so pairing
// it with the app's look would mean overriding most of it anyway. Utilities generated by Tailwind's
// `important: '#cirrus-body'` also outrank plain .rdp-* rules, which made mixing the two a
// specificity puzzle with no upside. `!` is used where a state has to beat the base day_button.
// The month back/forward arrows are identical; one string, used twice.
const NAV_BUTTON = 'cr-flex cr-h-7 cr-w-7 cr-cursor-pointer cr-items-center cr-justify-center cr-rounded-md '
  + 'cr-border-0 cr-bg-transparent cr-text-blue-900 hover:cr-bg-cirrusBlue-25 '
  + 'disabled:cr-cursor-default disabled:cr-opacity-30';

const CALENDAR_CLASS_NAMES = {
  root: 'cr-font-main cr-text-sm cr-font-medium cr-text-blue-900',
  months: 'cr-relative',
  month: 'cr-w-full',
  month_caption: 'cr-flex cr-h-9 cr-items-center cr-justify-center',
  caption_label: 'cr-font-main cr-text-base cr-font-semibold cr-text-blue-900',
  nav: 'cr-absolute cr-inset-x-0 cr-top-0 cr-flex cr-h-9 cr-items-center cr-justify-between',
  button_previous: NAV_BUTTON,
  button_next: NAV_BUTTON,
  chevron: 'cr-h-4 cr-w-4 cr-fill-current',
  // base.scss sets `table { width: 100% }`; cr-w-auto outranks it and keeps the grid at cell width.
  month_grid: 'cr-w-auto cr-border-collapse',
  weekday: 'cr-h-8 cr-w-9 cr-font-label cr-text-2xs cr-font-semibold cr-uppercase cr-text-blue-900/60',
  day: 'cr-h-9 cr-w-9 cr-p-0 cr-text-center',
  day_button:
    'cr-h-8 cr-w-8 cr-cursor-pointer cr-rounded-md cr-border-0 cr-bg-transparent cr-font-main '
    + 'cr-text-sm cr-font-medium cr-text-blue-900 hover:cr-bg-cirrusBlue-25',
  selected: '[&>button]:!cr-bg-cirrusBlue-400 [&>button]:!cr-text-white [&>button]:!cr-font-semibold',
  today: '[&>button]:cr-ring-1 [&>button]:cr-ring-inset [&>button]:cr-ring-cirrusBlue-100',
  outside: '[&>button]:cr-text-blue-900/30',
  disabled: '[&>button]:cr-cursor-default [&>button]:cr-opacity-40',
  hidden: 'cr-invisible',
};

export default function DatePickerField(props: {
  fieldName: string;
  required?: string;
  customClass?: string;
  enableDebouncedSave?: boolean;
  debouncedSaveFn?: (value: string) => Promise<void>;
  setIsSaving?: (isSaving: boolean) => void;
}) {
  const {
    fieldName,
    required,
    customClass = '',
    enableDebouncedSave = false,
    debouncedSaveFn,
    setIsSaving,
  } = props;

  const {
    control,
    formState: { errors },
  } = useFormContext();

  useDebouncedSave(fieldName, debouncedSaveFn, { setIsSaving, enabled: enableDebouncedSave });

  const error = get(errors, fieldName);
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const width = popoverRef.current?.offsetWidth ?? 300;
    const height = popoverRef.current?.offsetHeight ?? 340;
    // Flip above the field when there is not enough room below, and keep it inside the viewport
    // horizontally — the date card sits at the left edge on desktop and centred on phones.
    const openUpwards = rect.bottom + height > window.innerHeight && rect.top > height;
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
    setPosition({ top: openUpwards ? rect.top - height - 4 : rect.bottom + 4, left });
  }, []);

  useLayoutEffect(() => {
    if (isOpen) updatePosition();
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (popoverRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      setIsOpen(false);
    };
    // Capture phase so Escape closes the calendar without also reaching DetailCard's own keydown
    // listener, which would drop the whole card out of edit mode in the same keystroke.
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      setIsOpen(false);
      triggerRef.current?.focus();
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen, updatePosition]);

  const triggerClass = `cr-flex cr-items-center cr-justify-between cr-gap-2 cr-rounded-md cr-box-border
    cr-bg-white cr-px-2 cr-text-left cr-font-main cr-text-blue-900 cr-font-medium cr-cursor-pointer
    cr-w-full cr-max-w-full cr-h-[26px] ${customClass} ${
    error
      ? 'cr-border-red-500 cr-border-2 cr-outline-red-500 cr-outline-2'
      : 'cr-border cr-border-blue-900 focus:cr-outline-2 focus:cr-outline-cirrusBlue-400'
  }`;

  return (
    <Controller
      name={fieldName}
      control={control}
      rules={{ required }}
      render={({ field }) => {
        const parsed = field.value ? parse(field.value, VALUE_FORMAT, new Date()) : undefined;
        const selected = parsed && isValid(parsed) ? parsed : undefined;

        const commit = (date: Date | undefined) => {
          if (!date) return;
          field.onChange(format(date, VALUE_FORMAT));
          setIsOpen(false);
          triggerRef.current?.focus();
        };

        return (
          <div>
            <button
              ref={triggerRef}
              type="button"
              className={triggerClass}
              aria-haspopup="dialog"
              aria-expanded={isOpen}
              onClick={() => setIsOpen((open) => !open)}
              onBlur={field.onBlur}
            >
              <span>{selected ? selected.toLocaleDateString() : 'Select a date'}</span>
              <HiOutlineCalendarDays className="cr-shrink-0 cr-text-base cr-text-blue-900" />
            </button>
            {isOpen
              && createPortal(
                <div
                  ref={popoverRef}
                  className={`${OVERLAY_CLASS} cr-fixed cr-z-5000 cr-rounded-lg cr-border
                    cr-border-cirrusBlue-100 cr-bg-white cr-p-3 cr-shadow-lg`}
                  style={{ top: position.top, left: position.left }}
                  role="dialog"
                  aria-label="Choose date"
                >
                  <DayPicker
                    mode="single"
                    autoFocus
                    weekStartsOn={1}
                    selected={selected}
                    defaultMonth={selected}
                    onSelect={commit}
                    classNames={CALENDAR_CLASS_NAMES}
                  />
                  <div className="cr-mt-2 cr-flex cr-justify-end cr-border-0 cr-border-t cr-border-solid
                    cr-border-cirrusBlue-100 cr-pt-2">
                    <button
                      type="button"
                      className="cr-cursor-pointer cr-rounded-md cr-border-0 cr-bg-cirrusBlue-25 cr-px-3
                        cr-py-1 cr-font-main cr-text-sm cr-font-semibold cr-text-blue-900
                        hover:cr-bg-cirrusBlue-50"
                      onClick={() => commit(new Date())}
                    >
                      Today
                    </button>
                  </div>
                </div>,
                document.body,
              )}
            {error?.message && <p className="cr-text-xs cr-text-red-500">{String(error.message)}</p>}
          </div>
        );
      }}
    />
  );
}
